import React from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from '../Tooltip';

// Utility function to clean up object names
const cleanObjectName = (objectName: string): string => {
  return objectName
    .replace(/__c$/, '') // Remove __c suffix
    .replace(/__/g, ' ')  // Replace __ with space
    .replace(/_/g, ' ');  // Replace _ with space
};

interface TriggerCondition {
  field: string;
  conditions: string[];
}

interface TriggerFlow {
  operation: 'create' | 'update' | 'create_update';
  triggerFields: string[];
  conditions: Record<string, string[]>;
  flowName: string;
  flowId: string;
  performanceGrade?: string | null;
  performanceScore?: number | null;
}

interface TriggerData {
  [objectName: string]: TriggerFlow[];
}

interface FieldCentricTriggerTableProps {
  triggerData: TriggerData;
  orgId: string;
}

export function FieldCentricTriggerTable({ triggerData, orgId }: FieldCentricTriggerTableProps) {
  const navigate = useNavigate();

  const getOperationBadge = (operation: string) => {
    switch (operation) {
      case 'create':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
            Create
          </span>
        );
      case 'update':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
            Update
          </span>
        );
      case 'create_update':
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
            Create/Update
          </span>
        );
      default:
        return null;
    }
  };


  const handleFlowClick = (flowId: string) => {
    navigate(`/${orgId}/flow/${flowId}`);
  };

  // Sort objects alphabetically
  const sortedObjects = Object.entries(triggerData).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="bg-white shadow-sm rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <tbody className="bg-white">
          {sortedObjects.map(([objectName, flows], objectIndex) => (
            <React.Fragment key={objectName}>
              {/* Object Name Row */}
              <tr className="bg-gray-200">
                <td colSpan={4} className="px-4 py-2">
                  <span className="text-sm font-semibold text-gray-900">
                    {cleanObjectName(objectName)} <span className="text-xs font-normal text-gray-600">({flows.length} flow{flows.length !== 1 ? 's' : ''})</span>
                  </span>
                </td>
              </tr>
              {/* Column Headers Row */}
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="w-[22%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Any Change to
                </th>
                <th className="w-[22%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  That Results in
                </th>
                <th className="w-[40%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Flow
                </th>
                <th className="w-[16%] pr-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  Operation
                </th>
              </tr>
              {/* Group all flows by field first */}
              {(() => {
                // Collect all unique fields across all flows
                const fieldGroups = new Map<string, Array<{
                  flow: TriggerFlow;
                  conditions: string[];
                }>>();

                // Process each flow and group by field
                flows.forEach(flow => {
                  // Process trigger fields
                  flow.triggerFields.forEach(field => {
                    if (!fieldGroups.has(field)) {
                      fieldGroups.set(field, []);
                    }
                    fieldGroups.get(field)!.push({
                      flow,
                      conditions: flow.conditions[field] || []
                    });
                  });

                  // Process condition fields that aren't trigger fields
                  Object.entries(flow.conditions).forEach(([field, conditions]) => {
                    if (!flow.triggerFields.includes(field)) {
                      if (!fieldGroups.has(field)) {
                        fieldGroups.set(field, []);
                      }
                      fieldGroups.get(field)!.push({
                        flow,
                        conditions
                      });
                    }
                  });

                  // Handle flows with no fields
                  if (flow.triggerFields.length === 0 && Object.keys(flow.conditions).length === 0) {
                    if (!fieldGroups.has('—')) {
                      fieldGroups.set('—', []);
                    }
                    fieldGroups.get('—')!.push({
                      flow,
                      conditions: []
                    });
                  }
                });

                // Sort fields alphabetically
                const sortedFields = Array.from(fieldGroups.keys()).sort();
                let fieldColorIndex = 0;

                return sortedFields.map((field, fieldIndex) => {
                  const fieldFlows = fieldGroups.get(field)!;
                  const bgColor = fieldColorIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50';
                  fieldColorIndex++;

                  // Create rows for this field
                  const rows: JSX.Element[] = [];
                  let isFirstRowForField = true;

                  fieldFlows.forEach((item, itemIndex) => {
                    const { flow, conditions } = item;
                    const rowsForThisFlow = Math.max(1, conditions.length);

                    for (let condIndex = 0; condIndex < rowsForThisFlow; condIndex++) {
                      const isFirstRowForFlow = condIndex === 0;
                      const isVeryFirstDataRow = fieldIndex === 0 && itemIndex === 0 && condIndex === 0;
                      const key = `${field}-${itemIndex}-${condIndex}`;

                      rows.push(
                        <tr
                          key={key}
                          className={`${bgColor} hover:bg-gray-100 transition-colors`}
                        >
                          {/* Any Change to column - only show field on first row for this field */}
                          <td className={`px-4 text-sm text-gray-800 ${isVeryFirstDataRow ? 'pt-4 pb-2.5' : 'py-2.5'}`}>
                            <div className="pl-3">
                              {isFirstRowForField && field.length > 30 ? (
                                <Tooltip content={field} position="right">
                                  <span>{field}</span>
                                </Tooltip>
                              ) : (
                                isFirstRowForField ? field : ''
                              )}
                            </div>
                          </td>

                          {/* That Results in column */}
                          <td className={`px-4 text-sm text-gray-900 ${
                            isVeryFirstDataRow ? 'pt-4 pb-2.5' : 'py-2.5'
                          } ${itemIndex > 0 && condIndex === 0 ? 'border-t border-gray-200' : ''}`}>
                            {conditions[condIndex] ? (
                              <span className="text-gray-700">
                                <span className="font-normal">{field !== '—' ? field : ''}</span>
                                {field !== '—' && conditions[condIndex] && ' '}
                                {conditions[condIndex]}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>

                          {/* Flow column - only show on first row for this flow */}
                          {isFirstRowForFlow && (
                            <td className={`px-4 text-sm ${
                              isVeryFirstDataRow ? 'pt-4 pb-3' : 'py-3'
                            } ${itemIndex > 0 ? 'border-t border-gray-200' : ''}`} rowSpan={rowsForThisFlow}>
                              {flow.flowName.length > 40 ? (
                                <Tooltip content={flow.flowName} position="top">
                                  <a
                                    href={`/${orgId}/flow/${flow.flowId}`}
                                    onClick={(e) => {
                                      // Allow right-click and middle-click to work normally
                                      if (e.button !== 0 || e.metaKey || e.ctrlKey) {
                                        return;
                                      }
                                      e.preventDefault();
                                      handleFlowClick(flow.flowId);
                                    }}
                                    className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                                  >
                                    {flow.flowName}
                                  </a>
                                </Tooltip>
                              ) : (
                                <a
                                  href={`/${orgId}/flow/${flow.flowId}`}
                                  onClick={(e) => {
                                    // Allow right-click and middle-click to work normally
                                    if (e.button !== 0 || e.metaKey || e.ctrlKey) {
                                      return;
                                    }
                                    e.preventDefault();
                                    handleFlowClick(flow.flowId);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                                >
                                  {flow.flowName}
                                </a>
                              )}
                            </td>
                          )}
                          
                          {/* Operation column - only show on first row for this flow */}
                          {isFirstRowForFlow && (
                            <td className={`pr-3 whitespace-nowrap text-sm text-center ${
                              isVeryFirstDataRow ? 'pt-4 pb-3' : 'py-3'
                            } ${itemIndex > 0 ? 'border-t border-gray-200' : ''}`} rowSpan={rowsForThisFlow}>
                              {getOperationBadge(flow.operation)}
                            </td>
                          )}
                        </tr>
                      );
                      
                      if (isFirstRowForField) {
                        isFirstRowForField = false;
                      }
                    }
                  });

                  return rows;
                });
              })()}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}